import React, { useState, useMemo } from 'react';
import styles from './AddRecord.module.css';
import SwitchButton from './SwitchButton';
import ModalInputField from 'components/shared/modal/ModalInputField';
import ModalDropdown from 'components/shared/modal/ModalDropdown';
import { validateFieldName } from 'utils/validations';
import dataTypesConfig from '../../constants/dataTypes.json';

const AddRecord = ({ table, setTable, setNra, isArray, isIndex, isObject, dataTypes, customTypes = [] }) => {
    const [record, setRecord] = useState({
        name: "",
        type: "",
        is_index: isIndex || false,
        is_array: isArray || false,
        ai: false,
        is_auto_created: false,
        is_auto_updated: false,
        new: true
    });

    // Determine which types to show based on whether index is selected
    const availableTypes = useMemo(() => {
        if (record.is_index) {
            return dataTypesConfig.indexTypes;
        }
        const customTypeOptions = customTypes.map(ct => ({
            value: ct.name,
            label: ct.name
        }));
        if (isObject) {
            return customTypeOptions;
        }
        return [...dataTypesConfig.dataTypes, ...customTypeOptions];
    }, [record.is_index, customTypes, isObject]);

    const handleFieldChange = (key, value) => {
        setRecord(prev => {
            const updated = {
                ...prev,
                [key]: value
            };
            
            // If index selection changed, reset type if it's not compatible
            if (key === 'is_index') {
                const newAvailableTypes = value ? 
                    dataTypesConfig.indexTypes : 
                    [...dataTypesConfig.dataTypes, ...customTypes.map(ct => ({ value: ct.name, label: ct.name }))];
                const isCurrentTypeValid = newAvailableTypes.some(type => type.value === prev.type);
                
                if (!isCurrentTypeValid) {
                    updated.type = ''; // Reset type if it's not compatible
                }
            }
            
            // If type changed and it's not time_point, reset auto created/updated
            if (key === 'type' && value !== 'time_point') {
                updated.is_auto_created = false;
                updated.is_auto_updated = false;
            }
            
            return updated;
        });
    };

    const isUniqueFieldName = (name) => {
        const incoming = (name || '').trim();
        if (!incoming) return false;
        const existing = (table.fields || []).map(f => (f.name || '').trim());
        return !existing.includes(incoming);
    };

    const handleSave = () => {
        if (!record.name || !record.type) {
            alert("Name and type are required fields.");
            return;
        }

        const isValid = validateFieldName(record?.name);

        if (!isValid) { return; }

        const newField = {
            type: record.type,
            name: record.name,
            is_index: record.is_index,
            is_array: record.is_array,
            ai: false,
            new: true
        };

        const updatedFields = [...(table.fields || []), newField];

        const updatedTable = {
            ...table,
            fields: updatedFields
        };

        setTable(updatedTable);
        setNra(1);
    };

    return (
        <div className={styles.add_record}>
            <div className={styles.record_fields}>
                <div className={styles.field_group}>
                    <span>Index</span>
                    <SwitchButton
                        checked={record.is_index}
                        onChange={() => handleFieldChange('is_index', !record.is_index)}
                    />
                </div>

                <ModalInputField
                    placeholder="Name"
                    value={record.name}
                    setValue={(val) => handleFieldChange('name', val)}
                    validator={(val) => validateFieldName(val) && isUniqueFieldName(val)}
                />

                <ModalDropdown
                    title={record.type || 'Select type'}
                    options={availableTypes}
                    setOption={(val) => {
                        const selectedType = typeof val === 'object' ? val.value : val;
                        handleFieldChange('type', selectedType);
                    }}
                />


                {!isObject && <div className={styles.field_group}>
                    <span>Array</span>
                    <SwitchButton
                        checked={record.is_array}
                        onChange={() => handleFieldChange('is_array', !record.is_array)}
                    />
                </div>
                }


            </div>

            <div className={styles.record_actions}>
                <button onClick={() => setNra(1)}>Cancel</button>
                <button
                    onClick={handleSave}
                    disabled={!(validateFieldName(record?.name) && isUniqueFieldName(record?.name) && !!record?.type)}
                >
                    Save new record
                </button>
            </div>
        </div>
    );
};

export default AddRecord;